import queryString from "query-string";

import { axios } from "../api";

import type { HistoryPage, HistoryRow } from "./types";

/** Rows per page — also what the panel tells the user filters are scoped to. */
export const HISTORY_PAGE_SIZE = 20;

/**
 * GET /v6/users/history/ — response_model_by_alias=False (app/users/api.py:618),
 * so the wire envelope carries `items`, not the JSON:API `data` alias. Reading
 * `.data.data` here silently renders an empty feed on every tenant regardless
 * of row count — the exact bug fixed for the section-dashboard shell in
 * src/directory/__tests__/directoryActivityEnvelope.test.tsx. Falls back to
 * `.data.data` only in case a future deploy changes the alias setting, never
 * as the primary read.
 *
 * `sort=-created` is a real, documented query param (app/crud.py
 * common_parameters -> app/sorting.py get_sort). The route itself has no
 * default ordering (app/users/crud.py get_user_history has no order_by), so
 * "newest first" is asked for explicitly rather than assumed.
 */
export async function fetchUserHistoryPage(
  page: number,
  size: number = HISTORY_PAGE_SIZE
): Promise<HistoryPage> {
  const res = await axios.get("/v6/users/history/", {
    params: { page, size, sort: "-created" },
    cache: false,
    suppressErrorToast: true,
  });
  const data = res?.data ?? {};
  const rawItems = data.items ?? data.data;
  const items: HistoryRow[] = Array.isArray(rawItems) ? rawItems : [];
  return {
    items,
    total: typeof data.total === "number" ? data.total : items.length,
    page: typeof data.page === "number" ? data.page : page,
    size: typeof data.size === "number" ? data.size : size,
    pages: typeof data.pages === "number" ? data.pages : 0,
  };
}

/**
 * ONE record's audit trail — the read every History tab makes (BP-04, H244;
 * RecordHistoryTab.tsx). Scoped by the `source_type` + `source_id` filter PAIR.
 *
 * ⚠️ `paramsSerializer` is LOAD-BEARING (src/blank/blank.ts header): without
 * it axios sends `filter[]=`, FastAPI binds nothing, the request 200s, and
 * the tab shows EVERY record's history with no error. Envelope `items`, as
 * above. `sort=-id` is the order the rows were written in.
 */
export async function fetchRecordHistoryPage(
  sourceType: string,
  sourceId: number | string,
  page: number,
  size: number
): Promise<{ rows: HistoryRow[]; total: number }> {
  const res = await axios.get("/v6/users/history/", {
    params: {
      page,
      size,
      filter: [
        JSON.stringify({ field: "source_type", op: "==", value: sourceType }),
        JSON.stringify({ field: "source_id", op: "==", value: sourceId }),
      ],
      sort: "-id",
    },
    paramsSerializer: (p: Record<string, any>) => queryString.stringify(p),
    cache: false,
  } as any);
  const items = res?.data?.items;
  return {
    rows: Array.isArray(items) ? items : [],
    total: typeof res?.data?.total === "number" ? res.data.total : 0,
  };
}

/**
 * What ONE USER did to one kind of record: the user record's History tab
 * sections "Quotes", "Jobs" and "SORs" (src/users/user-management/HistoryTab.tsx).
 * Scoped by `created_by` + `source_type`, not by a record. That is why each
 * row's Action names the record it touched ("Updated · Job #42"), which the
 * old sections showed as a separate "Job ID" column.
 * Same serializer rule and `items` envelope as fetchRecordHistoryPage.
 */
export async function fetchUserActivityPage(
  userId: number | string,
  sourceType: string,
  recordNoun: string,
  page: number,
  size: number
): Promise<{ rows: HistoryRow[]; total: number }> {
  const res = await axios.get("/v6/users/history/", {
    params: {
      page,
      size,
      filter: [
        JSON.stringify({ field: "created_by", op: "==", value: userId }),
        JSON.stringify({ field: "source_type", op: "==", value: sourceType }),
      ],
      sort: "-id",
    },
    paramsSerializer: (p: Record<string, any>) => queryString.stringify(p),
    cache: false,
  } as any);
  const items: HistoryRow[] = Array.isArray(res?.data?.items)
    ? res.data.items
    : [];
  return {
    rows: items.map((r) => ({
      ...r,
      description: `${r.description ?? "Updated"} · ${recordNoun} #${
        r.source_id ?? "—"
      }`,
    })),
    total: typeof res?.data?.total === "number" ? res.data.total : 0,
  };
}
